import { Phone } from "lucide-react";

import type { Doc } from "@convex/_generated/dataModel";

import {
  IntegrationCardShell,
  StatusPill,
} from "@/components/dashboard/IntegrationCardShell";

interface TelephoneIntegrationPanelProps {
  linqNumber: Doc<"linq_numbers"> | null | undefined;
}

export function TelephoneIntegrationPanel({
  linqNumber,
}: TelephoneIntegrationPanelProps) {
  const degraded =
    linqNumber?.status === "active" &&
    (linqNumber.linqStatus === "FLAGGED" ||
      linqNumber.linqStatus === "SUSPENDED" ||
      linqNumber.linqHealth === "AT_RISK" ||
      linqNumber.linqHealth === "CRITICAL");

  const pill =
    linqNumber == null ? undefined : linqNumber.status === "pending" ? (
      <StatusPill variant="pending">Provisioning</StatusPill>
    ) : linqNumber.status === "registering" ? (
      <StatusPill variant="pending">Registering</StatusPill>
    ) : linqNumber.status === "active" ? (
      degraded ? (
        <StatusPill variant="attention">Needs attention</StatusPill>
      ) : (
        <StatusPill variant="active">Connected</StatusPill>
      )
    ) : undefined;

  return (
    <IntegrationCardShell
      icon={Phone}
      title="Telephone"
      pill={pill}
      description="Reach your agent over SMS / iMessage"
      footer={<FooterBlock linqNumber={linqNumber} degraded={degraded ?? false} />}
    />
  );
}

function FooterBlock({
  linqNumber,
  degraded,
}: {
  linqNumber: Doc<"linq_numbers"> | null | undefined;
  degraded: boolean;
}) {
  // Query still loading — neutral placeholder; no flicker to "not set up"
  if (linqNumber === undefined) {
    return (
      <div className="h-10 animate-pulse rounded-full bg-home-ink/[0.04] dark:bg-white/5" />
    );
  }

  // No number yet — a real action, not a lonely paragraph
  if (linqNumber === null) {
    return (
      <a
        href="mailto:help@clawcraft.ca?subject=Telephone%20number%20setup"
        className="block w-full rounded-full bg-home-ink/[0.05] px-4 py-2.5 text-center font-body text-sm font-semibold text-home-ink transition-all duration-200 hover:bg-home-ink/10 dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
      >
        Request a number
      </a>
    );
  }

  if (linqNumber.status === "pending") {
    return (
      <p className="py-1 font-body text-[13px] leading-relaxed text-home-ink/55 dark:text-white/55">
        Request filed — your number arrives within 1–3 business days.
      </p>
    );
  }

  if (linqNumber.status === "registering") {
    return (
      <p className="py-1 font-body text-[13px] leading-relaxed text-home-ink/55 dark:text-white/55">
        Carrier registration in progress. Your number will be live shortly.
      </p>
    );
  }

  if (linqNumber.status === "active") {
    return (
      <div>
        <p className="font-mono text-sm text-home-ink/80 dark:text-white/80">
          {linqNumber.phoneNumber}
        </p>
        {degraded && (
          <p className="mt-2 font-body text-[12px] leading-relaxed text-home-ink/55 dark:text-white/55">
            Delivery may be unreliable right now — contact help@clawcraft.ca if
            messages stop arriving.
          </p>
        )}
      </div>
    );
  }

  return null; // "disconnected" is filtered out server-side
}
